import React, { useState, useRef } from 'react';
import { convertImage } from './converterUtils';
import ImageUploader from '../../components/ImageUploader/ImageUploader';
import './Converter.css';

const Converter = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [convertedImage, setConvertedImage] = useState(null);
  const [originalFormat, setOriginalFormat] = useState('');
  const [targetFormat, setTargetFormat] = useState('png');
  const [quality, setQuality] = useState(90);
  const [isConverting, setIsConverting] = useState(false);
  const canvasRef = useRef(null);

  const formatOptions = [
    { value: 'png', label: 'PNG (Lossless)' },
    { value: 'jpeg', label: 'JPEG (Lossy)' },
    { value: 'webp', label: 'WebP (Modern)' }
  ];

  const handleImageSelected = (img) => {
    setOriginalImage(img);
    setConvertedImage(null);
    
    // Try to determine the format from the image src
    if (img.src) {
      const srcFormat = img.src.split(';')[0].split('/')[1] || '';
      setOriginalFormat(srcFormat || 'unknown');
    }
  };

  const handleFormatChange = (e) => {
    setTargetFormat(e.target.value);
    setConvertedImage(null);
  };

  const handleQualityChange = (e) => {
    setQuality(parseInt(e.target.value, 10));
    setConvertedImage(null);
  };

  const handleConvert = async () => {
    if (!originalImage) return;
    
    setIsConverting(true);
    try {
      const result = await convertImage(originalImage, targetFormat, quality, canvasRef.current);
      setConvertedImage(result);
    } catch (error) {
      console.error('Error converting image:', error);
    } finally {
      setIsConverting(false);
    }
  };

  const handleDownload = () => {
    if (!convertedImage) return;
    
    const link = document.createElement('a');
    link.href = convertedImage;
    link.download = `converted-image.${targetFormat}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isShowingQualitySlider = targetFormat === 'jpeg' || targetFormat === 'webp';

  return (
    <div className="converter tool-container">
      <div className="container">
        <div className="tool-header">
          <h1>Format Converter</h1>
          <p>Convert between JPG, PNG, and WebP formats</p>
        </div>
        
        <div className="tool-content">
          <ImageUploader 
            onImageSelected={handleImageSelected}
            selectedImage={originalImage}
          />
          
          {originalImage && (
            <div className="converter-controls">
              <div className="control-group">
                <label>Current Format: {originalFormat.toUpperCase()}</label>
              </div>
              
              <div className="control-group">
                <label htmlFor="format-select">Target Format:</label>
                <select 
                  id="format-select" 
                  value={targetFormat} 
                  onChange={handleFormatChange}
                  className="format-select"
                >
                  {formatOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {isShowingQualitySlider && (
                <div className="control-group">
                  <label htmlFor="quality">Quality: {quality}%</label>
                  <input 
                    type="range" 
                    id="quality" 
                    min="1" 
                    max="100" 
                    value={quality} 
                    onChange={handleQualityChange} 
                  />
                </div>
              )}
              
              <button 
                className="btn btn-primary convert-btn" 
                onClick={handleConvert}
                disabled={isConverting}
              >
                {isConverting ? 'Converting...' : 'Convert Image'}
              </button>
            </div>
          )}
          
          {originalImage && (
            <div className="image-preview-section">
              <div className="image-preview-container">
                <h3>Original ({originalFormat.toUpperCase()})</h3>
                <div className="image-preview">
                  <img src={originalImage.src} alt="Original" />
                </div>
              </div>
              
              {convertedImage && (
                <div className="image-preview-container">
                  <h3>Converted ({targetFormat.toUpperCase()})</h3>
                  <div className="image-preview">
                    <img src={convertedImage} alt="Converted" />
                  </div>
                  <button className="btn btn-primary download-btn" onClick={handleDownload}>
                    Download {targetFormat.toUpperCase()} Image
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Hidden canvas for image processing */}
          <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
        </div>
      </div>
    </div>
  );
};

export default Converter; 